"use client";

import { ChevronDown } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";

type Props = {
  readMore: string;
  readLess: string;
  children: React.ReactNode;
};

const COLLAPSED_MAX_HEIGHT = 420;

/**
 * Clamps long content on small screens (where the description competes with
 * everything below it) and adds a "read more" toggle. On lg+ the full text is
 * always shown.
 */
const DescriptionClamp: React.FC<Props> = ({ readMore, readLess, children }) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [clampable, setClampable] = useState(false);

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    setClampable(el.scrollHeight > COLLAPSED_MAX_HEIGHT + 60);
  }, []);

  const collapsed = clampable && !expanded;

  return (
    <div className="flex flex-col">
      <div
        ref={contentRef}
        style={collapsed ? { maxHeight: COLLAPSED_MAX_HEIGHT } : undefined}
        className={twMerge(
          "relative overflow-hidden lg:!max-h-none",
          collapsed &&
            "after:absolute after:inset-x-0 after:bottom-0 after:h-24 after:bg-gradient-to-t after:from-background after:to-transparent lg:after:hidden",
        )}
      >
        {children}
      </div>
      {clampable && (
        <button
          type="button"
          onClick={() => setExpanded((prev) => !prev)}
          className="mt-2 flex w-fit flex-row items-center gap-1 text-sm font-semibold text-foreground underline underline-offset-4 lg:hidden"
        >
          <span>{expanded ? readLess : readMore}</span>
          <ChevronDown
            className={twMerge(
              "size-4 transition-transform",
              expanded && "rotate-180",
            )}
          />
        </button>
      )}
    </div>
  );
};

export default DescriptionClamp;
